import React, { useState, useMemo } from 'react'

import { PlusOutlined, QuestionCircleFilled } from '@ant-design/icons'
import { Button, Tag, Tooltip, message } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import EcAccountConceptModal from './EcAccountConceptModal'
import { ModuleNameTag, ConnectionStatusTag, EnabledStatusTag } from './EcAccountTags'

import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import ListPageHeader, { type ListPageHeaderItem } from '@components/shared/ListPageHeader'
import { MultipleValue } from '@shared/table/Rules/component'

import { formatDateTime } from '@/utils/dateTime'
import { buildEcAccountPlatformOptions } from '@/constants/ecAccountPlatforms'

interface MerchantOption {
  id: string;
  name: string;
}

interface EcAccountListService extends TableService {
  delete: (id: string) => Promise<{ success: boolean; message?: string }>;
}

interface EcAccountListProps {
  service: EcAccountListService;
  role?: 'operator' | 'admin';
  merchants?: MerchantOption[];
  currentMerchant?: { id: string; name?: string } | null;
  hideMerchantUI?: boolean; // Operator single-merchant 模式使用
}

interface EcAccountRow {
  id: string;
  merchant_name?: string;
  module_name?: string;
  display_name?: string;
  connection_status?: 'connected' | 'disconnected' | 'never_tested' | string;
  enable_fetch_orders?: boolean;
  stock_push_enabled?: boolean;
  stock_percentage?: number | null;
  sales_channel_name?: string;
  updated_at?: string;
  [key: string]: unknown;
}

export default function EcAccountList({
  service,
  role = 'operator', // 'operator' | 'admin'
  merchants = [],
  currentMerchant = null,
  hideMerchantUI = false, // Operator single-merchant 模式使用
}: EcAccountListProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [lastModified, setLastModified] = useState(0)
  const [conceptModalVisible, setConceptModalVisible] = useState(false)

  // Handle OAuth callback flash messages from URL parameters
  React.useEffect(() => {
    const msgKey = searchParams.get('message_key')
    const type = searchParams.get('type')

    if (msgKey) {
      const translatedMsg = t(msgKey)
      switch (type) {
        case 'success':
          message.success(translatedMsg)
          break
        case 'error':
          message.error(translatedMsg)
          break
        case 'warning':
          message.warning(translatedMsg)
          break
        default:
          message.info(translatedMsg)
      }
      searchParams.delete('message_key')
      searchParams.delete('type')
      setSearchParams(searchParams, { replace: true })
    }
  }, [searchParams, setSearchParams, t])

  const handleDelete = async (id: string) => {
    try {
      const result = await service.delete(id)

      if (result.success) {
        message.success(t('common.message.delete_success'))
        setLastModified(Date.now())
      } else {
        message.error(result.message || t('common.error.operation_failed'))
      }
    } catch (error) {
      console.error('Delete error:', error)
      message.error(t('common.error.operation_failed'))
    }
  }

  const columns = useMemo(() => {
    const cols: unknown[] = []

    if (role === 'operator' && !hideMerchantUI) {
      cols.push({
        nameKey: 'models.ec_account.merchant',
        tooltip: t('shared.ec_accounts.list.column_tooltips.merchant'),
        selector: ({ merchant_name }: EcAccountRow) => merchant_name || '-',
      })
    }

    cols.push(
      {
        nameKey: 'models.ec_account.module_name',
        sortable: true,
        sortFieldName: 'ec_accounts.module_name',
        cell: ({ module_name }: EcAccountRow) => <ModuleNameTag moduleName={module_name as string} />,
      },
      {
        nameKey: 'models.ec_account.display_name',
        sortable: true,
        sortFieldName: 'ec_accounts.display_name',
        selector: ({ display_name }: EcAccountRow) => display_name || '-',
      },
      {
        nameKey: 'models.ec_account.connection_status',
        cell: ({ connection_status }: EcAccountRow) => (
          <ConnectionStatusTag status={connection_status as string} />
        ),
      },

      {
        nameKey: 'models.ec_account.enable_fetch_orders',
        cell: ({ enable_fetch_orders }: EcAccountRow) => (
          <EnabledStatusTag value={!!enable_fetch_orders} />
        ),
      },
      {
        nameKey: 'models.ec_account.stock_push_enabled',
        cell: ({ stock_push_enabled, stock_percentage }: EcAccountRow) => {
          if (!stock_push_enabled) return <EnabledStatusTag value={false} />;
          return <Tag color="success">{stock_percentage != null ? `${stock_percentage}%` : '-'}</Tag>;
        },
      },
      {
        nameKey: 'admin.ec_accounts.form.sales_channel',
        selector: ({ sales_channel_name }: EcAccountRow) => sales_channel_name || '-',
      },
      {
        nameKey: 'common.updated_at',
        sortable: true,
        sortFieldName: 'ec_accounts.updated_at',
        width: '150px',
        selector: ({ updated_at }: EcAccountRow) => formatDateTime(updated_at as string),
      },
      {
        nameKey: 'common.actions',
        cell: (record: EcAccountRow) => (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => navigate(`/ec_accounts/${record.id}`)}
          >
            {t('common.button.view')}
          </Button>
        ),
      }
    )

    return cols
  }, [t, role, navigate, hideMerchantUI])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    rules.push({
      label: t('models.ec_account.display_name'),
      component: MultipleValue,
      inputName: 'ec_accounts.display_name',
      createable: true,
      defaultPinned: true,
      tooltip: {
        title: t('shared.ec_accounts.list.filter_tooltips.display_name.title'),
        content: t('shared.ec_accounts.list.filter_tooltips.display_name.content'),
      },
    })

    if (role === 'operator' && !hideMerchantUI && merchants.length > 0) {
      rules.push({
        label: t('models.ec_account.merchant'),
        component: MultipleValue,
        inputName: 'merchants.id',
        createable: false,
        defaultPinned: true,
        defaultOptions: merchants.map(merchant => ({
          value: merchant.id,
          label: merchant.name,
        })),
        tooltip: {
          title: t('shared.ec_accounts.list.filter_tooltips.merchant.title'),
          content: t('shared.ec_accounts.list.filter_tooltips.merchant.content'),
        },
      })
    }

    rules.push({
      label: t('models.ec_account.module_name'),
      component: MultipleValue,
      inputName: 'ec_accounts.module_name',
      createable: false,
      defaultPinned: true,
      defaultOptions: buildEcAccountPlatformOptions(t),
      tooltip: {
        title: t('shared.ec_accounts.list.filter_tooltips.module_name.title'),
        content: t('shared.ec_accounts.list.filter_tooltips.module_name.content'),
      },
    })

    rules.push({
      label: t('models.ec_account.connection_status'),
      component: MultipleValue,
      inputName: 'ec_accounts.connection_status',
      createable: false,
      defaultOptions: [
        { value: 'connected', label: t('enums.ec_account.connection_status.connected') },
        { value: 'disconnected', label: t('enums.ec_account.connection_status.disconnected') },
        { value: 'never_tested', label: t('enums.ec_account.connection_status.never_tested') },
      ],
    })

    return rules
  }, [merchants, t, role, hideMerchantUI])

  const defaultRules = useMemo(() => {
    if (role === 'operator') {
      if (currentMerchant?.id) {
        return {
          'merchants.id': {
            type: 'is',
            value: [currentMerchant.id]
          }
        }
      }
      if (merchants.length === 1) {
         return {
          'merchants.id': {
            type: 'is',
            value: [merchants[0].id]
          }
        }
      }
    }
    return {}
  }, [currentMerchant, merchants, role])

  // Determine header items based on role
  const headerItems: ListPageHeaderItem[] = role === 'admin'
    ? ['platform', 'platform.ec_accounts']
    : [{ label: t('operator.menu.ec_accounts') }]

  const basePath = role === 'admin' ? '/admin' : undefined
  const tableId = role === 'admin' ? 'AdminEcAccountsTable' : 'OperatorEcAccountsTable'

  // defaultRules / defaultRulesKey / newItemUrl 不在 TableIndexProps 上，但原 .jsx 有傳，
  // 為保留 runtime 行為（轉換 ≠ 重構）以未型別化 spread 通過，由 TableIndex 內部自行處理。
  const extraTableProps = {
    defaultRules,
    defaultRulesKey: currentMerchant?.id || (merchants.length === 1 ? merchants[0].id : 'default'),
    newItemUrl: '/ec_accounts/new',
  } as Record<string, unknown>;

  return (
    <>
      <ListPageHeader
        basePath={basePath}
        items={headerItems}
        guideButton={
          <Tooltip title={t('concepts.ec_accounts.tooltip')}>
            <Button
              type="text"
              size="small"
              icon={<QuestionCircleFilled style={{ color: '#1890ff' }} />}
              onClick={() => setConceptModalVisible(true)}
              style={{ padding: 0, height: 'auto', minWidth: 'auto' }}
            />
          </Tooltip>
        }
        actions={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/ec_accounts/new')}
          >
            {t('common.button.new')}
          </Button>
        }
      />
      <TableIndex
        columns={columns}
        searchable={{
          match: 'contains',
          searchFields: [
            t('models.ec_account.display_name'),
            t('models.ec_account.module_name'),
          ],
        }}
        columnsEditable={true}
        tableId={tableId}
        defaultQuery={{
          sorting_field: 'ec_accounts.updated_at',
          sorting_direction: 'desc',
        }}
        supportRules={supportRules}
        lastModified={lastModified}
        setLastModified={setLastModified}
        service={service}
        filterHelpExample={t('shared.ec_accounts.list.filter_help_example') as string}
        {...extraTableProps}
      />
      <EcAccountConceptModal
        visible={conceptModalVisible}
        onClose={() => setConceptModalVisible(false)}
      />
    </>
  )
}
